import { useEffect, useState } from "react";
import useSWR from "swr";
import { ArrowDown, ArrowUp, Eye, Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fetcher, imgUrl } from "../../lib/api";
import { PageHeader, Panel, Field, GoldButton, GhostButton } from "../../components/admin/AdminUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import ImageUpload from "../../components/admin/ImageUpload";
import ConfirmDelete from "../../components/admin/ConfirmDelete";
import BannerCarousel from "../../components/public/BannerCarousel";

const PRESETS = [
  ["none", "Sem botão / link", ""], ["catalog", "Catálogo completo", "/catalogo"],
  ["masc", "Masculinos", "/catalogo?gender=masculino"], ["fem", "Femininos", "/catalogo?gender=feminino"],
  ["uni", "Unissex", "/catalogo?gender=unissex"], ["promo", "Promoções", "/catalogo?tag=promo"],
  ["launch", "Lançamentos", "/catalogo?tag=launch"], ["best", "Mais vendidos", "/catalogo?tag=best"],
  ["product", "Perfume específico", null], ["custom", "Link personalizado", null],
];
const EMPTY = { image: "", title: "", description: "", button_label: "", link: "", active: true };

const presetOf = (link) => {
  if (!link) return "none";
  const p = PRESETS.find((x) => x[2] === link);
  if (p) return p[0];
  return link.startsWith("/perfume/") ? "product" : "custom";
};

function BannerForm({ open, onOpenChange, banner, onSaved, interval }) {
  const [f, setF] = useState(EMPTY);
  const [preset, setPreset] = useState("none");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const { data: products } = useSWR(open ? "/admin/products" : null, fetcher);

  useEffect(() => {
    if (open) {
      setF(banner ? { ...EMPTY, ...banner } : EMPTY);
      setPreset(presetOf(banner?.link));
    }
  }, [open, banner]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const choosePreset = (v) => {
    setPreset(v);
    const p = PRESETS.find((x) => x[0] === v);
    if (p[2] !== null) setF((s) => ({ ...s, link: p[2], button_label: v === "none" ? "" : s.button_label || "Conferir" }));
    else setF((s) => ({ ...s, link: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Envie a imagem do banner.");
    if (f.link && !f.button_label) return toast.error("Informe o texto do botão.");
    const body = { image: f.image, title: f.title, description: f.description, button_label: f.link ? f.button_label : "", link: f.link, active: f.active };
    setSaving(true);
    try {
      if (banner) await api.put(`/admin/banners/${banner.id}`, body);
      else await api.post("/admin/banners", body);
      toast.success("Banner salvo");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#D4AF37]/20 bg-[#141417]" data-testid="banner-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium">{banner ? "Editar banner" : "Novo banner"}</DialogTitle>
          <DialogDescription>Recomendado: imagem horizontal com pelo menos 1920 × 1080 px.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Field label="Imagem *"><ImageUpload value={f.image} onChange={set("image")} testid="banner-image" aspect="aspect-video" /></Field>
          <Field label="Título"><Input value={f.title} onChange={set("title")} data-testid="banner-form-title" /></Field>
          <Field label="Descrição"><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="banner-form-description" /></Field>
          <Field label="Direcionamento do botão">
            <Select value={preset} onValueChange={choosePreset}>
              <SelectTrigger data-testid="banner-form-link-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {preset === "product" && (
            <Field label="Perfume">
              <Select value={f.link || undefined} onValueChange={set("link")}>
                <SelectTrigger data-testid="banner-form-product"><SelectValue placeholder="Escolha o perfume" /></SelectTrigger>
                <SelectContent>{(products || []).map((p) => <SelectItem key={p.id} value={`/perfume/${p.slug}`}>{p.name} — {p.brand}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {preset === "custom" && <Field label="Link" hint="Página do site (começando com /) ou endereço https://"><Input value={f.link} onChange={set("link")} placeholder="https://" data-testid="banner-form-link" /></Field>}
          {preset !== "none" && <Field label="Texto do botão"><Input value={f.button_label} onChange={set("button_label")} maxLength={40} data-testid="banner-form-button-label" /></Field>}
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={set("active")} data-testid="banner-form-active" />Publicado no site</label>
          <div className="flex flex-wrap justify-end gap-3">
            <GhostButton type="button" disabled={!f.image} onClick={() => setPreview(true)} data-testid="banner-form-preview"><Eye className="h-4 w-4" />Visualizar prévia</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="banner-form-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Salvar</GoldButton>
          </div>
        </form>
        <PreviewDialog open={preview} onOpenChange={setPreview} banners={[{ ...f, id: "draft" }]} interval={interval} />
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ open, onOpenChange, banners, interval }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl overflow-hidden border-[#D4AF37]/20 bg-[#0B0B0C] p-0" data-testid="banner-preview-dialog">
        <DialogTitle className="sr-only">Prévia</DialogTitle>
        <DialogDescription className="sr-only">Prévia do carrossel</DialogDescription>
        {open && <BannerCarousel banners={banners} interval={interval} />}
      </DialogContent>
    </Dialog>
  );
}

export default function Banners() {
  const { data, mutate } = useSWR("/admin/banners", fetcher);
  const { data: settings, mutate: mutateSettings } = useSWR("/admin/settings", fetcher);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [preview, setPreview] = useState(false);
  const [interval, setIntervalVal] = useState(6);

  useEffect(() => {
    if (settings) setIntervalVal(settings.banner_interval);
  }, [settings]);

  const list = data || [];
  const call = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const move = (i, d) => {
    const ids = list.map((b) => b.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    call(() => api.put("/admin/banners/reorder", { ids }));
  };
  const saveInterval = async () => {
    try {
      await api.put("/admin/settings", { ...settings, banner_interval: Number(interval) });
      mutateSettings();
      toast.success("Tempo de transição salvo");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-banners">
      <PageHeader title="Banners" subtitle="Carrossel da página inicial — alterações aparecem no site automaticamente">
        <GhostButton onClick={() => setPreview(true)} disabled={!list.some((b) => b.active)} data-testid="banners-preview-all"><Eye className="h-4 w-4" />Prévia do carrossel</GhostButton>
        <GoldButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-banner-button"><Plus className="h-4 w-4" />Novo banner</GoldButton>
      </PageHeader>
      <Panel className="mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Tempo de transição (segundos)"><Input type="number" min={2} max={30} value={interval} onChange={(e) => setIntervalVal(e.target.value)} className="w-40" data-testid="banner-interval-input" /></Field>
          <GhostButton onClick={saveInterval} disabled={!settings} data-testid="banner-interval-save">Salvar tempo</GhostButton>
        </div>
      </Panel>
      {data && list.length === 0 && <p className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-[#A1A1AA]">Nenhum banner cadastrado. Enquanto isso, o site exibe a imagem padrão da página inicial.</p>}
      <div className="space-y-3">
        {list.map((b, i) => (
          <div key={b.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/5 bg-[#141417] p-4" data-testid={`admin-banner-row-${b.id}`}>
            <div className="flex flex-col gap-1">
              <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 text-[#A1A1AA] hover:text-[#F3E5AB] disabled:opacity-20" aria-label="Subir" data-testid={`banner-up-${b.id}`}><ArrowUp className="h-4 w-4" /></button>
              <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="rounded p-1 text-[#A1A1AA] hover:text-[#F3E5AB] disabled:opacity-20" aria-label="Descer" data-testid={`banner-down-${b.id}`}><ArrowDown className="h-4 w-4" /></button>
            </div>
            <img src={imgUrl(b.image)} alt="" className="h-20 w-36 rounded-lg object-cover" />
            <div className="min-w-[160px] flex-1">
              <p className="font-serif text-xl">{b.title || "(sem título)"}</p>
              <p className="truncate text-xs text-[#A1A1AA]">{b.button_label ? `${b.button_label} → ${b.link}` : "Sem botão"}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA]"><Switch checked={b.active} onCheckedChange={(v) => call(() => api.put(`/admin/banners/${b.id}`, { ...b, active: v }))} data-testid={`banner-active-${b.id}`} />{b.active ? "Publicado" : "Oculto"}</label>
            <button onClick={() => { setEditing(b); setOpen(true); }} className="rounded-full p-2 text-[#A1A1AA] hover:text-[#F3E5AB]" aria-label="Editar" data-testid={`banner-edit-${b.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => setToDelete(b)} className="rounded-full p-2 text-[#A1A1AA] hover:text-red-400" aria-label="Excluir" data-testid={`banner-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <BannerForm open={open} onOpenChange={setOpen} banner={editing} onSaved={mutate} interval={interval} />
      <PreviewDialog open={preview} onOpenChange={setPreview} banners={list.filter((b) => b.active)} interval={interval} />
      <ConfirmDelete open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={() => { call(() => api.delete(`/admin/banners/${toDelete.id}`), "Banner excluído"); setToDelete(null); }} title="Excluir banner?" description="O banner será removido do carrossel." />
    </div>
  );
}
